import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Pagination as MuiPagination,
  Alert,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import ListPageLayout from '../components/shared/list-page-layout';
import ListFilters, { FilterField } from '../components/shared/list-filters';
import { CardGridSkeleton } from '../components/shared/skeletons';
import { IListNewsRequest } from '../../model/base/request/list-news-request';
import { useNewsController } from './context/news-controller';
import { useNewsState } from './context/news-context';
import { useNavigate } from 'react-router-dom';

export function NewsComponent() {
  const navigate = useNavigate();
  const { loadData, loadCategories } = useNewsController();
  const { news, categories, loading, loadingFilters } = useNewsState();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<IListNewsRequest>({
    page: 0,
    pageSize: 12,
    term: '',
    categoryId: undefined,
    startDate: '',
    endDate: '',
  });

  // Carregar categorias ao montar o componente
  useEffect(() => {
    loadCategories();
    loadData(filters);
  }, []);

  // Opções de filtro dinâmicas
  const categoryOptions = categories.map(cat => ({
    value: cat.id,
    label: cat.description
  }));

  const filterFields: FilterField[] = [
    {
      name: 'term',
      label: 'Pesquisar',
      type: 'text',
      placeholder: 'Buscar por título',
      fullWidth: false,
      gridSize: { xs: 12, sm: 12, md: 4 },
    },
    {
      name: 'categoryId',
      label: 'Categoria',
      type: 'select',
      options: categoryOptions,
      disabled: loadingFilters,
      gridSize: { xs: 12, sm: 6, md: 3 },
    },
    {
      name: 'startDate',
      label: 'Data Inicial',
      type: 'date',
      gridSize: { xs: 12, sm: 6, md: 2.5 },
    },
    {
      name: 'endDate',
      label: 'Data Final',
      type: 'date',
      gridSize: { xs: 12, sm: 6, md: 2.5 },
    },
  ];

  const handleFilterChange = (name: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    const searchFilters = { ...filters, page: 0 };
    setCurrentPage(0);
    setFilters(searchFilters);
    loadData(searchFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: IListNewsRequest = {
      page: 0,
      pageSize: 12,
      term: '',
      categoryId: undefined,
      startDate: '',
      endDate: '',
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadData(clearedFilters);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadData(newFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleNewsClick = (id?: number, category?: string) => {
    if (id) {
      const categorySlug = category?.toLowerCase().replace(/\s+/g, '-') || 'noticia';
      navigate(`/noticias/${categorySlug}/${id}`);
    }
  };

  return (
    <ListPageLayout
      title="Notícias"
      subtitle="Fique por dentro das últimas notícias do município"
      breadcrumbs={[
        { label: 'Notícias' },
      ]}
      filters={
        <ListFilters
          fields={filterFields.map((field) => ({
            ...field,
            value: filters[field.name as keyof IListNewsRequest],
          }))}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={loading}
        />
      }
    >
      {loading && !news.hasItems() ? (
        <CardGridSkeleton items={12} hasImage={true} hasActions={false} />
      ) : !news.hasItems() ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nenhuma notícia encontrada com os filtros aplicados.
        </Alert>
      ) : (
        <>
          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            <Grid
              container
              spacing={3}
              sx={{
                opacity: loading ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              {news.items.map((newsItem) => (
                <Grid item xs={12} sm={6} md={4} key={newsItem.id}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 6,
                      },
                    }}
                    onClick={() => handleNewsClick(newsItem.id, newsItem.category)}
                  >
                    {newsItem.urlImageEmphasis ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={newsItem.urlImageEmphasis}
                        alt={newsItem.title || 'Notícia'}
                        sx={{
                          objectFit: 'cover',
                        }}
                        onError={(e: any) => {
                          e.target.style.display = 'none';
                          const fallbackBox = e.target.nextSibling;
                          if (fallbackBox) fallbackBox.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {(!newsItem.urlImageEmphasis || true) && (
                      <Box
                        sx={{
                          height: '200px',
                          backgroundColor: '#f5f5f5',
                          display: newsItem.urlImageEmphasis ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography color="text.secondary" variant="body2">
                          Sem imagem
                        </Typography>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      {newsItem.category && (
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            icon={<CategoryIcon />}
                            label={newsItem.category}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      )}
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          minHeight: '3em',
                        }}
                      >
                        {newsItem.title || 'Sem título'}
                      </Typography>
                      {newsItem.desFont && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            flexGrow: 1,
                          }}
                        >
                          {newsItem.desFont}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 'auto',
                          color: 'text.secondary',
                        }}
                      >
                        <CalendarTodayIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                          {formatDate(newsItem.publishedDate || newsItem.date)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {news.pageCount > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
                mb: 2,
              }}
            >
              <MuiPagination
                count={news.pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
    </ListPageLayout>
  );
}
